import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  Games as GamesIcon,
} from '@mui/icons-material';

const TLMASMADProject: React.FC = () => {
  return (
    <Box>
      {/* Header avec titre et image */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <img
          src="/TV/TLMASMAD.png"
          alt="Logo Tout le monde a son mot à dire"
          style={{
            width: '64px',
            height: '64px',
            objectFit: 'contain',
            filter: 'drop-shadow(2px 2px 4px rgba(167, 139, 250, 0.3))',
          }}
        />
        <Typography
          variant="h4"
          component="h2"
          sx={{
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold'
          }}
        >
          Tout le monde a son mot à dire
        </Typography>
      </Box>

      {/* Statut et date */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            2018-2020
          </Typography>
        </Box>
      </Box>

      {/* Description */}
      <Typography
        variant="body1"
        sx={{ mb: 3, lineHeight: 1.6 }}
      >
        Gestion technique d'une des émissions les plus polyvalentes du 
        paysage audiovisuel français. Mission de maintenance approfondie des nombreux jeux 
        existants et développement de nouvelles mécaniques sur l'architecture éprouvée 
        déjà en place, assurant évolutivité et fiabilité.
      </Typography>

      <Divider sx={{ my: 3 }} />

      {/* Missions réalisées */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold'
          }}
        >
          Missions réalisées
        </Typography>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2
        }}>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Maintenance multiples jeux existants
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Développement nouveaux jeux
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Optimisation architecture existante
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Gestion diversité mécaniques
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Évolution système établi
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Intégration nouvelles fonctionnalités
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Support production continue
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              • Documentation technique
            </Typography>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Domaines techniques */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold'
          }}
        >
          Domaines techniques
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {['Systèmes multi-jeux', 'Architecture éprouvée', 'Développement évolutif', 'Maintenance avancée', 'Intégration continue', 'Support production'].map((tech) => (
            <Chip
              key={tech}
              label={tech}
              variant="filled"
              size="small"
              sx={{
                background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.1) 0%, rgba(20, 184, 166, 0.1) 100%)',
                color: '#a78bfa',
                border: '1px solid rgba(167, 139, 250, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, rgba(167, 139, 250, 0.2) 0%, rgba(20, 184, 166, 0.2) 100%)',
                },
              }}
            />
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Défis techniques */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            background: 'linear-gradient(135deg, #a78bfa 0%, #14b8a6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: 'bold'
          }}
        >
          Défis techniques relevés
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Gestion multi-jeux complexe :</strong> Maintenir et faire évoluer un 
          écosystème diversifié de jeux avec des mécaniques variées, en assurant la 
          cohérence et la stabilité de l'ensemble du système.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Évolution sur architecture établie :</strong> Développer de nouvelles 
          fonctionnalités en respectant les contraintes et les standards d'un système 
          éprouvé, tout en apportant des améliorations substantielles.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Continuité de service :</strong> Assurer une maintenance transparente 
          pendant la production continue d'une émission historique avec des attentes 
          de fiabilité maximale.
        </Typography>
        <Typography variant="body2">
          <strong>Expertise métier approfondie :</strong> Maîtriser les spécificités 
          techniques d'une émission ancienne avec ses particularités historiques, 
          ses contraintes spécifiques et ses exigences de performance.
        </Typography>
      </Box>
    </Box>
  );
};

export default TLMASMADProject;
